export type JobValidationStep = 'role' | 'qualifications' | 'skills' | 'settings';

export interface JobValidationField {
  field: string;
  step: JobValidationStep;
  message: string;
}

export interface JobValidationErrorShape {
  code: 'JOB_VALIDATION_FAILED' | 'JOB_ENTITLEMENT_FAILED' | 'JOB_GOVERNANCE_SNAPSHOT_FAILED' | string;
  message: string;
  fields: JobValidationField[];
  draftId?: string;
}

const JOB_PUBLISH_ERROR_TRANSLATION_KEYS: Record<string, string> = {
  JOB_VALIDATION_FAILED: 'hire.createJob.publishErrors.validation',
  JOB_ENTITLEMENT_FAILED: 'hire.createJob.publishErrors.entitlement',
  JOB_GOVERNANCE_SNAPSHOT_FAILED: 'hire.createJob.publishErrors.governance',
  JOB_CONFIGURATION_ERROR: 'hire.createJob.publishErrors.configuration',
  JOB_FORBIDDEN: 'hire.createJob.publishErrors.forbidden',
  JOB_ORGANIZATION_INACTIVE: 'hire.createJob.publishErrors.organizationInactive',
  JOB_DRAFT_STATE_CONFLICT: 'hire.createJob.publishErrors.draftStateConflict',
  JOB_NOT_FOUND: 'hire.createJob.publishErrors.notFound',
};

export function getJobPublishErrorTranslationKey(code: string): string {
  return JOB_PUBLISH_ERROR_TRANSLATION_KEYS[code] || 'hire.createJob.publishErrors.unknown';
}

export class JobValidationError extends Error {
  code = 'JOB_VALIDATION_FAILED' as const;
  fields: JobValidationField[];

  constructor(fields: JobValidationField[], message = buildJobValidationMessage(fields)) {
    super(message);
    this.name = 'JobValidationError';
    this.fields = fields;
  }
}

type PublishableJob = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isJobValidationStep = (value: unknown): value is JobValidationStep =>
  value === 'role' || value === 'qualifications' || value === 'skills' || value === 'settings';

const hasText = (value: unknown) => typeof value === 'string' && value.trim().length > 0;
const isNumberInRange = (value: unknown, min: number, max: number) =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
const isPositiveInteger = (value: unknown) =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

const requirementsText = (requirementsJson: unknown, key: 'text' | 'responsibilities') => {
  if (!requirementsJson || typeof requirementsJson !== 'object') return '';
  const value = (requirementsJson as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : '';
};

export function validateJobForPublish(job: PublishableJob): JobValidationField[] {
  const fields: JobValidationField[] = [];
  const add = (field: string, step: JobValidationStep, message: string) => fields.push({ field, step, message });

  if (!hasText(job.title)) add('title', 'role', 'Job title is required.');
  if (!hasText(job.department)) add('department', 'role', 'Department is required.');
  if (!hasText(job.location)) add('location', 'role', 'Location is required.');
  if (!hasText(job.job_type)) add('job_type', 'role', 'Job type is required.');
  if (!hasText(job.employment_type)) add('employment_type', 'role', 'Employment type is required.');
  if (!hasText(job.seniority)) add('seniority', 'role', 'Seniority level is required.');

  if (!hasText(job.description)) add('description', 'qualifications', 'Job description is required.');
  if (!hasText(requirementsText(job.requirements_json, 'text'))) {
    add('requirements', 'qualifications', 'Requirements are required.');
  }
  if (!hasText(requirementsText(job.requirements_json, 'responsibilities'))) {
    add('responsibilities', 'qualifications', 'Key responsibilities are required.');
  }

  if (!isNumberInRange(job.min_experience_years, 0, 50)) {
    add('min_experience_years', 'skills', 'Minimum years of experience must be between 0 and 50.');
  }
  if (!hasText(job.education_level)) add('education_level', 'skills', 'Minimum education level is required.');

  if (!isNumberInRange(job.cv_pass_threshold, 0, 100)) {
    add('cv_pass_threshold', 'settings', 'CV pass score must be between 0 and 100.');
  }
  if (!isNumberInRange(job.interview_pass_threshold, 0, 100)) {
    add('interview_pass_threshold', 'settings', 'Interview pass score must be between 0 and 100.');
  }
  if (!isPositiveInteger(job.open_duration_days)) {
    add('open_duration_days', 'settings', 'Posting duration is required.');
  }
  if (!isPositiveInteger(job.number_of_openings)) {
    add('number_of_openings', 'settings', 'At least one opening is required.');
  }
  if (job.visibility_mode !== 'direct_link_only' && job.visibility_mode !== 'seek_public') {
    add('visibility_mode', 'settings', 'Job visibility is required.');
  }
  if (!hasText(job.public_slug)) add('public_slug', 'settings', 'Application link slug is required.');
  if (!hasText(job.default_assignee)) {
    add('default_assignee', 'settings', 'Assigned recruiter is required.');
  }

  return fields;
}

export function buildJobValidationMessage(fields: JobValidationField[]): string {
  if (fields.length === 0) return 'Job is ready to publish.';
  const names = fields.slice(0, 3).map((field) => field.message.replace(/\.$/, ''));
  const suffix = fields.length > 3 ? ` and ${fields.length - 3} more` : '';
  return `Please complete ${names.join(', ')}${suffix}.`;
}

export function toJobValidationErrorShape(fields: JobValidationField[]): JobValidationErrorShape {
  return {
    code: 'JOB_VALIDATION_FAILED',
    message: buildJobValidationMessage(fields),
    fields,
  };
}

export function normalizeJobValidationError(error: unknown): JobValidationErrorShape | null {
  if (error instanceof JobValidationError) {
    return toJobValidationErrorShape(error.fields);
  }

  if (error && typeof error === 'object') {
    const value = error as { code?: string; message?: string; fields?: unknown; details?: unknown; error?: unknown; draftId?: string };
    if (typeof value.code === 'string' && Array.isArray(value.fields)) {
      return {
        code: value.code,
        message: value.message || buildJobValidationMessage(value.fields as JobValidationField[]),
        fields: value.fields as JobValidationField[],
        draftId: value.draftId,
      };
    }
    if (value.error) return normalizeJobValidationError(value.error);
    if (
      value.code === '42883'
      || (typeof value.message === 'string' && value.message.includes('job_publish_validation_fields'))
    ) {
      return {
        code: 'JOB_CONFIGURATION_ERROR',
        message: 'Job publishing is temporarily unavailable. Your draft is safe; please try again later or contact support.',
        fields: [{
          field: 'configuration',
          step: 'settings',
          message: 'Job publishing is temporarily unavailable.',
        }],
        draftId: value.draftId,
      };
    }
    if (value.message === 'JOB_VALIDATION_FAILED' || value.code === '23514') {
      const details = typeof value.details === 'string' ? value.details : '';
      const fields = parseDbValidationFields(details);
      if (fields.length > 0) return toJobValidationErrorShape(fields);
    }
    if (value.code === 'P0403' || (typeof value.message === 'string' && value.message.includes('EntitlementError:'))) {
      return {
        code: 'JOB_ENTITLEMENT_FAILED',
        message: 'This job was saved as a draft, but publishing requires an active plan allowance or wallet credits.',
        fields: [{
          field: 'entitlement',
          step: 'settings',
          message: 'Publishing requires an active plan allowance or wallet credits.',
        }],
        draftId: value.draftId,
      };
    }
    if (typeof value.message === 'string' && value.message.includes('governance snapshot')) {
      return {
        code: 'JOB_GOVERNANCE_SNAPSHOT_FAILED',
        message: 'This job was saved as a draft, but publishing could not create its governance snapshot.',
        fields: [{
          field: 'governance',
          step: 'settings',
          message: 'Publishing could not create the job governance snapshot.',
        }],
        draftId: value.draftId,
      };
    }
  }

  return null;
}

function parseDbValidationFields(details: string): JobValidationField[] {
  try {
    const parsed: unknown = JSON.parse(details);
    const fields = isRecord(parsed) && Array.isArray(parsed.fields)
      ? parsed.fields
      : Array.isArray(parsed)
        ? parsed
        : [];
    return fields.filter(
      (field): field is JobValidationField =>
        isRecord(field)
        && typeof field.field === 'string'
        && isJobValidationStep(field.step)
        && typeof field.message === 'string',
    );
  } catch {
    return [];
  }
}
